/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useGetBatchContactImport, useGetPromptRevision} from '@agent-assist/api-typescript-react-query-hooks';
import {TABLE_FILTER_IDS} from '@agent-assist/common/lib/admin/table-filters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Accordion, AccordionDetails, AccordionSummary, Box, Link, Stack} from '@mui/material';
import Typography from '@mui/material/Typography';
import type {GridColDef, GridFilterModel, GridRowGroupingModel} from '@mui/x-data-grid-premium';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FC, useEffect, useState, useRef} from 'react';
import {Link as RouterLink, useNavigate, useParams} from 'react-router-dom';
import {ErrorPage} from './error/error-page';
import ContactImportProgressBar from '../components/ContactImportProgressBar';
import {ContactModal} from '../components/ContactModal';
import {ContactsImportHeader} from '../components/ContactsImportHeader';
import {PageContainer} from '../components/PageContainer';
import {ScreenLoader} from '../components/ScreenLoader';
import {type DateRangeValue, SuperDatePicker} from '../components/SuperTable/SuperDatePicker';
import SuperTable from '../components/SuperTable/SuperTable';
import type {FeatureFlagSet} from '../flags';
import {useGetContactsListFiltered} from '../hooks/abstractions/useGetContactsListFiltered';
import {useDynamicHeight} from '../hooks/useDynamicHeight';
import {useSuperTableOptions} from '../hooks/useSuperTableOptions';
import {ROUTES} from '../routes';
import {formatDateCommon} from '../services/dates';
import { LoadingSpinner } from '../components/LoadingSpinner';

export interface ProgressCounts {
  readonly totalCount: number;
  readonly processedCount: number;
}

export const ContactImportViewPage: FC = () => {
  const {contactImportId} = useParams<{contactImportId: string}>();
  const tableId = `${TABLE_FILTER_IDS.CONTACT_IMPORT_TABLE}`;
  if (!contactImportId) throw new Error('No contactImportId');
  const navigate = useNavigate();
  const {functionImportStatus} = useFlags<FeatureFlagSet>();

  const {get, isError} = useGetContactsListFiltered();
  const [options, updateOptions] = useSuperTableOptions(tableId);

  const [allRows, setAllRows] = useState<any[]>([]);
  const [isLoadingAllContacts, setIsLoadingAllContacts] = useState(false);
  const [isPartiallyLoading, setIsPartiallyLoading] = useState(false);
  const [expectedTotalRows, setExpectedTotalRows] = useState<number | null>(null);
  const [totalProcessed, setTotalProcessed] = useState<number>(0);
  const [totalFailedContacts, setTotalFailedContacts] = useState<number>(0);
  const [totalCompletedContacts, setTotalCompletedContacts] = useState<number>(0);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [dateRange, setDateRange] = useState<DateRangeValue>([null, null]);
  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>();
  const [modalContactId, setModalContactId] = useState<string>();

  const contactImport = useGetBatchContactImport({contactImportId});
  const promptRevision = useGetPromptRevision({criteriaId: contactImport.data?.promptRevisionId!}, {enabled: false});

  useEffect(() => {
    if (contactImportId && !isLoadingAllContacts && contactImport.data?.promptRevisionId) {
      const filterModelIsEmpty = !filterModel?.items?.length;
      const effectiveFilter =
        filterModelIsEmpty && options.reapplyFilter && options.lastFilter ? options.lastFilter : filterModel;
      setAllRows([]);
      setIsLoadingAllContacts(true);
      setIsPartiallyLoading(false);

      const fetchAllContacts = async () => {
        try {
          let accumulatedColumns: GridColDef[] = [];
          let accumulatedRows: Record<string, any>[] = [];

          const firstPageResult = await get({
            contactImportId: contactImportId,
            filterModel: effectiveFilter,
            dateRange,
            limit: options.rowsLimit,
            pageIndex: 0,
          });
          setIsPartiallyLoading(true);
          accumulatedRows = [...firstPageResult.rows];
          accumulatedColumns = [...firstPageResult.columns];
          setColumns(accumulatedColumns);
          setAllRows(accumulatedRows);

          const totalPages = firstPageResult.totalPages || 1;
          const totalRecords = firstPageResult.totalRecords || 0;
          const failedContacts = firstPageResult.totalFailedContacts || 0;
          const completedContacts = firstPageResult.totalCompletedContacts || 0;
          setTotalProcessed(totalRecords);
          setTotalFailedContacts(failedContacts);
          setTotalCompletedContacts(completedContacts);
          const fetchTotalRows = firstPageResult.totalRecords ?? 0;
          const totalRows = fetchTotalRows > options.rowsLimit ? options.rowsLimit : fetchTotalRows;
          setExpectedTotalRows(totalRows);
          if (totalPages > 1) {
            for (let pageNum = 1; pageNum < totalPages; pageNum++) {
              console.log(`---fetching page ${pageNum}`);
              const pageResult = await get({
                contactImportId: contactImportId,
                filterModel: effectiveFilter,
                dateRange,
                limit: options.rowsLimit,
                pageIndex: pageNum,
                totalPages,
                totalRecords,
                totalFailedContacts: failedContacts,
                totalCompletedContacts: completedContacts,
              });
              accumulatedRows = [...accumulatedRows, ...pageResult.rows];
              setAllRows(accumulatedRows);
            }
          }
        } catch (e) {
          console.error(e);
        } finally {
          setIsLoadingAllContacts(false);
          setIsPartiallyLoading(false);
          updateHeight();
        }
      };
      void fetchAllContacts();
    }
  }, [contactImportId, filterModel, dateRange, options.rowsLimit, contactImport.data?.promptRevisionId]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const {height: tableHeight, updateHeight} = useDynamicHeight(tableContainerRef, 48);

  useEffect(() => {
    updateHeight();
  }, [allRows.length]);

  const handleRowClick = (params: any, prefersModal: boolean) => {
    if (prefersModal) {
      setModalContactId(params.id);
    } else {
      if (contactImportId) {
        navigate(ROUTES.CONTACT_VIEW(contactImportId, params.id));
      }
    }
  };

  const onCloseContactModal = () => {
    setModalContactId(undefined);
  };

  const handleApplyFilter = (newFilterModel?: GridFilterModel, newGroupingModel?: GridRowGroupingModel) => {
    setFilterModel(newFilterModel);
    setGroupingModel(newGroupingModel);
  };

  const handleDateRangeChange = (newDateRange: DateRangeValue) => {
    setDateRange(newDateRange);
  };

  const handleRowGroupingModelChange = (newModel: GridRowGroupingModel) => {
    setGroupingModel(newModel);
  };

  const [liveProgress, setLiveProgress] = useState<ProgressCounts>({
    totalCount: 0,
    processedCount: 0,
  });

  useEffect(() => {
    const {totalCount, totalNeedProcessing} = contactImport.data ?? {};
    if (!(typeof totalCount === 'number' && typeof totalNeedProcessing === 'number')) return; // no data to set...
    setLiveProgress({
      totalCount,
      processedCount: totalCount - totalNeedProcessing,
    });
  }, [contactImport.isLoading, contactImport.isRefetching, liveProgress.processedCount, liveProgress.totalCount]);

  if (contactImport.isError) {
    return <ErrorPage errors={[contactImport.error]} />;
  }

  if (contactImport.isLoading) {
    return <ScreenLoader />;
  }

  const actions = <SuperDatePicker onDateRangeSelected={handleDateRangeChange} />;

  const subtitle =
    typeof contactImport.data.processedCount === 'number' && typeof contactImport.data.totalCount === 'number' ? (
      <Stack spacing={3} direction={'row'}>
        {functionImportStatus && (
          isLoadingAllContacts ? (
            <LoadingSpinner size='sm' />
          ) : (
          <ContactImportProgressBar
            processedCount={totalCompletedContacts}
            totalCount={totalFailedContacts + totalCompletedContacts}
            status={contactImport.data.status}
          />
          )
        )}
        <Link to={`/analytics/${contactImportId}`} component={RouterLink} sx={{textTransform: 'capitalize'}}>
          View Insights
        </Link>
      </Stack>
    ) : (
      formatDateCommon(contactImport.data.createdAt)
    );

  return (
    <PageContainer title={contactImport.data.name} subtitle={subtitle} actions={actions}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction={'row'} spacing={3}>
            <Typography color={'text.secondary'}>
              <strong>Created:</strong> {formatDateCommon(contactImport.data.createdAt)}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <ContactsImportHeader
            contactImport={contactImport}
            contactImportId={contactImportId}
            liveProgress={liveProgress}
            showLive={!!contactImport.data.totalNeedProcessing}
            promptRevisionData={promptRevision.data}
            totalProcessed={totalProcessed}
            totalFailedContacts={totalFailedContacts}
            totalCompletedContacts={totalCompletedContacts}
            loading={isLoadingAllContacts}
          />
        </AccordionDetails>
      </Accordion>
      <Box ref={tableContainerRef}>
        <SuperTable
          columns={columns}
          rows={allRows}
          onRowClick={handleRowClick}
          onApplyFilter={handleApplyFilter}
          rowGroupingModel={groupingModel}
          onRowGroupingModelChange={handleRowGroupingModelChange}
          isLoading={isLoadingAllContacts}
          isError={isError}
          isPartiallyLoading={isPartiallyLoading}
          expectedTotalRows={expectedTotalRows}
          // apply the contact import id
          tableId={tableId}
          height={tableHeight ? `${tableHeight}px` : undefined}
          options={options}
          updateOptions={updateOptions}
        />
      </Box>
      {!!contactImportId && !!modalContactId && (
        <ContactModal
          contactImportId={contactImportId}
          contactId={modalContactId}
          closeContactModal={onCloseContactModal}
        />
      )}
    </PageContainer>
  );
};
